<template>
  <div v-click-outside="closeDropdown" class="relative">
    <div
      ref="triggerRef"
      class="flex items-center cursor-pointer p-2 rounded-full hover:bg-grey-9 dark:hover:bg-dark-surface-2 transition-colors"
      role="button"
      aria-label="Switch between apps"
      :aria-expanded="isOpen"
      :aria-controls="dropdownId"
      tabindex="0"
      @click="toggleDropdown"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
    >
      <div class="flex items-center space-x-2">
        <mdicon name="apps" :size="20" class="text-grey-11 dark:text-dark-ink-muted" />
      </div>
    </div>

    <!-- Dropdown Content -->
    <Teleport to="body">
      <Transition
        name="app-switcher"
        @enter="onEnter"
        @after-enter="onAfterEnter"
        @leave="onLeave"
        @after-leave="onAfterLeave"
      >
        <div
          v-if="isOpen"
          id="app-switcher-dropdown"
          ref="dropdownRef"
          data-click-outside-ignore
          :class="[
            'fixed z-50 bg-white dark:bg-dark-surface rounded-lg shadow-xl flex flex-col transition-all duration-200 ease-out',
            'lg:rounded-lg inset-0 lg:inset-auto rounded-none lg:w-[400px] lg:h-[500px]',
          ]"
          :style="dropdownStyle"
          role="dialog"
          aria-modal="true"
          aria-label="App Switcher"
        >
          <!-- Header (Mobile) -->
          <div
            class="flex items-center justify-between px-4 pt-4 shrink-0 lg:hidden"
          >
            <h2 class="text-lg font-medium text-grey-1 dark:text-dark-ink">Labour Apps</h2>
            <button
              class="p-2 rounded-full hover:bg-grey-9 dark:hover:bg-dark-surface-2 transition-colors"
              aria-label="Close app switcher"
              @click="closeDropdown"
            >
              <mdicon name="close" :size="20" class="text-grey-5 dark:text-dark-ink-muted" />
            </button>
          </div>

          <!-- Search Input -->
          <div class="p-4 shadow-xs shrink-0">
            <AppInput
              v-model="searchTerm"
              placeholder="Search apps..."
              icon="magnify"
              :show-clear-button="true"
            />
          </div>

          <!-- App Grid -->
          <div class="p-4 flex-1 overflow-y-auto">
            <TransitionGroup
              v-if="filteredApps.length"
              name="app-list"
              tag="div"
              class="grid grid-cols-3 gap-3"
            >
              <AppSwitcherItem
                v-for="app in filteredApps"
                :key="app.id"
                :app="app"
                :is-pinned="userLabourAppStore.userHasPinnedApp(app.id)"
                :class="[
                  userLabourAppStore.userHasPinnedApp(app.id)
                    ? 'order-first'
                    : '',
                ]"
                @toggle-pin="handleTogglePin"
                @close-switcher="closeDropdown"
              />
            </TransitionGroup>
            <div v-else class="flex flex-col items-center justify-center h-full text-grey-5 dark:text-dark-ink-muted">
              <mdicon name="apps" :size="48" class="mb-2 opacity-50" />
              <p class="text-sm">No apps found</p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Backdrop -->
    <Teleport to="body">
      <Transition name="backdrop">
        <div
          v-if="isOpen"
          class="fixed inset-0 z-40 bg-black/20 transition-opacity duration-200"
          aria-hidden="true"
          @click="closeDropdown"
        ></div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch
} from 'vue'
import { useUserLabourAppStore } from '../../../stores/userLabourAppStore.js'
import AppInput from '../input/AppInput.vue'
import AppSwitcherItem from './AppSwitcherItem.vue'

const userLabourAppStore = useUserLabourAppStore()

const triggerRef = ref(null)
const dropdownRef = ref(null)
const isOpen = ref(false)
const searchTerm = ref('')

const MOBILE_BREAKPOINT = 1024

const filteredApps = computed(() => {
  if (!searchTerm.value) {
    return userLabourAppStore.apps || []
  }

  return userLabourAppStore.apps.filter((app) =>
    app.title.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
})

const dropdownStyle = ref({})

const toggleDropdown = async () => {
  if (!isOpen.value) {
    if (!userLabourAppStore.apps.length) {
      await userLabourAppStore.fetchApps()
    }
  }

  isOpen.value = !isOpen.value

  if (isOpen.value) {
    await nextTick()
    positionDropdown()
    searchTerm.value = ''
  }
}

const closeDropdown = () => {
  isOpen.value = false
  searchTerm.value = ''
}

const positionDropdown = () => {
  if (!triggerRef.value || !dropdownRef.value) return

  if (window.innerWidth < MOBILE_BREAKPOINT) {
    dropdownStyle.value = {}
    return
  }

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth

  const dropdownWidth = 400
  const dropdownHeight = 500

  let top, left

  top = triggerRect.bottom + 8
  left = triggerRect.left

  if (left + dropdownWidth > viewportWidth - 16) {
    left = viewportWidth - dropdownWidth - 16
  }

  if (top + dropdownHeight > viewportHeight - 16) {
    top = triggerRect.top - dropdownHeight - 8
  }

  dropdownStyle.value = {
    top: `${Math.max(16, top)}px`,
    left: `${Math.max(16, left)}px`,
  }
}

const handleTogglePin = async (appId, isPinned) => {
  await userLabourAppStore.updateUserHasPinnedAppStatus(appId, isPinned)
}

const handleResize = () => {
  if (isOpen.value) {
    nextTick(() => positionDropdown())
  }
}

const onEnter = (el) => {
  if (window.innerWidth >= MOBILE_BREAKPOINT) {
    el.style.opacity = '0'
    el.style.transform = 'scale(0.95) translateY(-10px)'
  } else {
    el.style.opacity = '0'
    el.style.transform = 'translateY(100%)'
  }
}

const onAfterEnter = (el) => {
  el.style.opacity = ''
  el.style.transform = ''
}

const onLeave = (el) => {
  if (window.innerWidth >= MOBILE_BREAKPOINT) {
    el.style.opacity = '0'
    el.style.transform = 'scale(0.95) translateY(-10px)'
  } else {
    el.style.opacity = '0'
    el.style.transform = 'translateY(100%)'
  }
}

const onAfterLeave = (el) => {
  el.style.opacity = ''
  el.style.transform = ''
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => positionDropdown())
  }
})
</script>

<style scoped>
@media (min-width: 1024px) {
  .app-switcher-enter-active {
    transition: all 0.2s ease-out;
  }

  .app-switcher-leave-active {
    transition: all 0.15s ease-in;
  }

  .app-switcher-enter-from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  .app-switcher-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
}

@media (max-width: 1023px) {
  .app-switcher-enter-active {
    transition: all 0.3s ease-out;
  }

  .app-switcher-leave-active {
    transition: all 0.25s ease-in;
  }

  .app-switcher-enter-from {
    opacity: 0;
    transform: translateY(100%);
  }

  .app-switcher-leave-to {
    opacity: 0;
    transform: translateY(100%);
  }
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* App list transitions for pinning/unpinning */
.app-list-move {
  transition: transform 0.3s ease;
}

.app-list-enter-active,
.app-list-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.app-list-enter-from,
.app-list-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
